/**
 * Whether a cited law has drifted since the chat cited it.
 *
 * The wire value is typed loosely on purpose (see AgentV2LrrReferenceSchema): the backend
 * may gain states, and an unrecognised one must render nothing rather than fail validation.
 * `null` is the common case and means "say nothing" — the law is unchanged, or the backend
 * had no baseline to compare against, or its lookup could not complete.
 */
export const LrrContentStatus = {
  Stale: 'stale',
  Removed: 'removed',
} as const

export type DisplayableLrrContentStatus = (typeof LrrContentStatus)[keyof typeof LrrContentStatus]

export const kLrrContentStatusDefinitions: Record<DisplayableLrrContentStatus, { label: string; heading: string; body: string[] }> = {
  [LrrContentStatus.Stale]: {
    label: 'Updated since cited',
    heading: 'This reference has been updated',
    body: [
      'This reference is still valid, but the underlying statute, rule, or regulation has been updated since this chat started',
      'Start a new chat for updated references.',
    ],
  },
  [LrrContentStatus.Removed]: {
    label: 'Missing reference',
    heading: 'Double check this reference',
    body: ['This reference cannot be found. It may have been repealed or renumbered since this chat started.', 'Start a new chat for updated references.'],
  },
}

/** Narrows an unrecognised or absent status to "render nothing". */
export function isDisplayableLrrContentStatus(status: string | null | undefined): status is DisplayableLrrContentStatus {
  return status === LrrContentStatus.Stale || status === LrrContentStatus.Removed
}
